/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { MouseEventHandler } from "react";

import {
  AlertIcon,
  CheckCircleIcon,
  CloseIcon,
  colors,
  InfoIcon,
  WarningIcon
} from "@dotnet/telematics-design-system";

import { ALERT_STATUS } from "../../context/alert/types";
import { useStyles } from "./styles";

export interface IAlertProps {
  status: ALERT_STATUS;
  title: string;
  description?: string;
  close?: MouseEventHandler<SVGSVGElement>;
}

const Alert = ({ status, title, description, close }: IAlertProps) => {
  const { blue700, green600, orange600, red700 } = colors;

  const {
    alertBox,
    alertContent,
    alertTitle,
    alertDescription,
    closeAlert,
    infoIcon
  } = useStyles();

  const styleOptions = {
    info: {
      border: blue700,
      icon: <InfoIcon css={css(infoIcon)} data-testid="alert-info-icon" />
    },
    critical: {
      border: red700,
      icon: (
        <AlertIcon
          css={css({ color: red700 })}
          data-testid="alert-critical-icon"
        />
      )
    },
    warning: {
      border: orange600,
      icon: <WarningIcon color={orange600} data-testid="alert-warning-icon" />
    },
    success: {
      border: green600,
      icon: (
        <CheckCircleIcon color={green600} data-testid="alert-success-icon" />
      )
    }
  };

  const borderColor = styleOptions[status]?.border;
  const iconImage = styleOptions[status]?.icon;

  return (
    <div
      data-testid="alert"
      css={css([{ borderLeft: `4px solid ${borderColor}` }, alertBox])}
    >
      {iconImage}

      <div css={css(alertContent)}>
        <div css={css(alertTitle)}>{title}</div>

        {description && <div css={css(alertDescription)}>{description}</div>}
      </div>

      {close && <CloseIcon onClick={close} css={css(closeAlert)} />}
    </div>
  );
};

export default Alert;
